import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { flexRender, Row, RowData, Table } from '@tanstack/react-table';

interface Props<T> {
  table: Table<T>;
  title?: string;
  renderCard?: (row: Row<T>, index: number) => React.ReactElement;
}

export const CheckoutFailureTable = <T extends RowData>({ table, title, renderCard }: Props<T>) => {
  const isMobile = useIsScreenSize(BREAKPOINTS.LG);

  if (isMobile && isDefined(renderCard)) {
    return (
      <div>
        {isDefined(title) && <h3 className="mb-5 heading-3">{title}</h3>}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-table-mobile">
          {table.getRowModel().rows.map((row, index) => renderCard(row, index))}
        </div>
      </div>
    );
  }

  return (
    <table className="border-separate border-spacing-x-0 border-spacing-y-2">
      <colgroup>
        {table.getAllLeafColumns().map((column, index) => (
          <col key={index} style={{ minWidth: column.columnDef.meta?.minWidth, width: column.columnDef.meta?.width }} />
        ))}
      </colgroup>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(columnHeader => (
              <th
                key={columnHeader.id}
                className="whitespace-nowrap pb-3 pr-5 text-left text-neutral-40 body-2 first:text-neutral-20 first:heading-3 last:pr-4"
              >
                {flexRender(columnHeader.column.columnDef.header, columnHeader.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.getVisibleCells().map(cell => (
              <td
                key={cell.id}
                className="bg-white py-3 pr-5 first:rounded-bl-regular first:rounded-tl-regular first:pl-4 last:rounded-br-regular last:rounded-tr-regular last:pr-4"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
